// mobile_v1: responsive — form 1 col + role grid 2x2 + agent cards
import React, { useState, useEffect } from 'react';
import API from '../api';
import { Users, Plus, Edit2, Trash2, Save, X, Power, Shield, ChevronDown, Radio } from 'lucide-react';
import toast from 'react-hot-toast';
import UsageBanner, { useUsageFull } from '../components/UsageBanner'; // usage_banner_ag

// perms_v2: Full sidebar coverage
const PERMISSIONS = [
  { key:'inbox', label:'📥 ดู Inbox / ตอบแชท' },
  { key:'reply_chat', label:'💬 ตอบแชท' },
  { key:'manage_kb', label:'🤖 จัดการ KB (เทรนบอท)' },
  { key:'view_kpi', label:'📊 ดู KPI' },
  { key:'view_time_report', label:'🕐 ดูรายงานเวลา' },
  { key:'manage_channels', label:'📡 จัดการ Channels' },
  { key:'manage_quick_replies', label:'⚡ จัดการ Quick Replies' },
  { key:'manage_agents', label:'👥 จัดการ Admin' },
  { key:'manage_telegram', label:'✉️ จัดการ Telegram' },
  { key:'manage_hot_games', label:'🎮 จัดการเกม' },
  { key:'broadcast', label:'📣 บรอดแคสต์ (ส่งข้อความกลุ่ม)' },
  { key:'ai_assistant', label:'✨ ผู้ช่วย AI (ตั้งค่า/ความรู้/โทเคน)' },
  { key:'settings', label:'⚙️ ตั้งค่าระบบ' },
  { key:'delete_history', label:'🗑️ ลบประวัติ' },
];

const ROLES = [
  { value:'owner', label:'Owner', color:'#f59e0b', desc:'ผู้ดูแลสูงสุด ทุกสิทธิ์' },
  { value:'admin', label:'Admin', color:'#a855f7', desc:'สิทธิ์เต็ม ยกเว้น Settings' },
  { value:'staff', label:'Staff', color:'#0ea5e9', desc:'ตอบแชท + KPI' },
  { value:'viewer', label:'Viewer', color:'#64748b', desc:'ดูอย่างเดียว' },
];

const emptyForm = { name:'', email:'', password:'', role:'staff', permissions:{}, channel_ids:[] };

export default function Agents() {
  const [agents, setAgents] = useState([]);
  const [channels, setChannels] = useState([]);
  const [roleDefaults, setRoleDefaults] = useState({});
  const [form, setForm] = useState(emptyForm);
  const [editId, setEditId] = useState(null);
  const agentsFull = useUsageFull('agents'); // usage_banner_ag
  const [showForm, setShowForm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [expandedId, setExpandedId] = useState(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  useEffect(() => {
    const h = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', h);
    return () => window.removeEventListener('resize', h);
  }, []);

  const load = async () => {
    const [{ data: ag }, { data: rd }, { data: ch }] = await Promise.all([
      API.get('/api/agents'),
      API.get('/api/agents/role-defaults'),
      API.get('/api/channels'),
    ]);
    setAgents(ag);
    setRoleDefaults(rd);
    setChannels(ch);
  };

  useEffect(() => { load(); }, []);

  const handleRoleChange = (role) => {
    setForm({ ...form, role, permissions: { ...(roleDefaults[role] || {}) } });
  };

  const handleSave = async () => {
    if (!form.name || !form.email || (!editId && !form.password)) {
      toast.error('กรุณากรอกข้อมูลให้ครบ'); return;
    }
    setLoading(true);
    try {
      if (editId) {
        await API.put(`/api/agents/${editId}`, form);
        toast.success('อัปเดตสำเร็จ');
      } else {
        await API.post('/api/agents', form);
        toast.success('เพิ่ม Admin สำเร็จ');
      }
      setForm(emptyForm); setEditId(null); setShowForm(false); load();
    } catch (err) { toast.error(err.response?.data?.error || 'เกิดข้อผิดพลาด'); }
    setLoading(false);
  };

  const handleEdit = (agent) => {
    setEditId(agent.id);
    setForm({
      name: agent.name,
      email: agent.email,
      password: '',
      role: agent.role,
      permissions: agent.permissions || roleDefaults[agent.role] || {},
      channel_ids: agent.permissions?.channel_ids || []
    });
    setShowForm(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleDelete = async (id) => {
    if (!window.confirm('ลบ Admin นี้?')) return;
    await API.delete(`/api/agents/${id}`);
    toast.success('ลบแล้ว'); load();
  };

  const handleToggle = async (id) => {
    await API.patch(`/api/agents/${id}/toggle`);
    load();
  };

  const getRoleInfo = (role) => ROLES.find(r => r.value === role) || ROLES[1];

  return (
    <div style={{ padding: isMobile ? '16px 12px' : '24px 28px', background:'#f8fafc', minHeight:'100%' }}>
      <UsageBanner resource="agents" /> {/* usage_banner_ag */}
      {/* Header */}
      <div style={{
        display:'flex',
        flexDirection: isMobile ? 'column' : 'row',
        alignItems: isMobile ? 'stretch' : 'center',
        justifyContent:'space-between',
        gap: isMobile ? 12 : 0,
        marginBottom: isMobile ? 16 : 24,
      }}>
        <div style={{ display:'flex', alignItems:'center', gap:10 }}>
          <Users size={isMobile ? 22 : 26} color="#0ea5e9" />
          <div>
            <h1 style={{ margin:0, fontSize: isMobile ? 18 : 22, fontWeight:700, color:'#1e293b' }}>จัดการ Admin</h1>
            <p style={{ margin:0, fontSize: isMobile ? 12 : 13, color:'#64748b' }}>กำหนดสิทธิ์และ Role ของแต่ละคน</p>
          </div>
        </div>
        <button onClick={() => { setForm(emptyForm); setEditId(null); setShowForm(true); }}
          style={{
            display:'flex', alignItems:'center', justifyContent: 'center', gap:6,
            background:'#0ea5e9', color:'#fff', border:'none', borderRadius:8,
            padding: isMobile ? '12px 18px' : '10px 18px',
            fontSize: isMobile ? 14 : 13, fontWeight:600, cursor:'pointer',
          }}>
          <Plus size={isMobile ? 16 : 15} />เพิ่ม Admin
        </button>
      </div>

      {/* Form */}
      {showForm && (
        <div style={{
          background:'#fff', borderRadius:14,
          padding: isMobile ? 16 : 28,
          boxShadow:'0 1px 6px rgba(0,0,0,0.07)',
          marginBottom: isMobile ? 16 : 24,
        }}>
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom: 16 }}>
            <h2 style={{ margin:0, fontSize: isMobile ? 14 : 15, fontWeight:600, color:'#1e293b' }}>
              {editId ? '✏️ แก้ไข Admin' : '➕ เพิ่ม Admin ใหม่'}
            </h2>
            <button onClick={() => { setShowForm(false); setEditId(null); }}
              style={{ background:'none', border:'none', cursor:'pointer', padding: 4 }}>
              <X size={20} color="#94a3b8" />
            </button>
          </div>

          {/* Basic Info — 1 col บน mobile */}
          <div style={{
            display:'grid',
            gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr 1fr',
            gap: isMobile ? 12 : 14,
            marginBottom: isMobile ? 16 : 20,
          }}>
            <div>
              <Label>ชื่อ</Label>
              <input value={form.name} onChange={e => setForm({...form, name:e.target.value})}
                placeholder="ชื่อ Admin" style={inputStyle(isMobile)} />
            </div>
            <div>
              <Label>อีเมล</Label>
              <input value={form.email} onChange={e => setForm({...form, email:e.target.value})}
                placeholder="email@example.com" disabled={!!editId}
                style={{ ...inputStyle(isMobile), opacity: editId ? 0.6 : 1 }} />
            </div>
            <div>
              <Label>{editId ? 'รหัสผ่านใหม่ (ถ้าต้องการเปลี่ยน)' : 'รหัสผ่าน'}</Label>
              <input type="password" value={form.password} onChange={e => setForm({...form, password:e.target.value})}
                placeholder="••••••••" style={inputStyle(isMobile)} />
            </div>
          </div>

          {/* Role Selection — 2x2 grid บน mobile */}
          <div style={{ marginBottom: isMobile ? 16 : 20 }}>
            <Label>Role</Label>
            <div style={{
              display: 'grid',
              gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(4, 1fr)',
              gap: isMobile ? 8 : 10,
              marginTop: 8,
            }}>
              {ROLES.map(r => (
                <div key={r.value} onClick={() => handleRoleChange(r.value)}
                  style={{
                    padding: isMobile ? '10px 12px' : '12px 16px',
                    borderRadius:10,
                    border:`2px solid ${form.role === r.value ? r.color : '#e2e8f0'}`,
                    background: form.role === r.value ? r.color+'11' : '#f8fafc',
                    cursor:'pointer', transition:'all 0.15s',
                  }}>
                  <div style={{
                    fontWeight:700,
                    color: form.role === r.value ? r.color : '#475569',
                    fontSize: isMobile ? 13 : 14,
                  }}>{r.label}</div>
                  <div style={{
                    fontSize: isMobile ? 11 : 12,
                    color:'#94a3b8',
                    marginTop:2,
                  }}>{r.desc}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Permissions — 1 col บน mobile */}
          <div style={{ marginBottom: isMobile ? 16 : 20 }}>
            <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:10 }}>
              <Shield size={14} color="#64748b" />
              <label style={{ fontSize:12, fontWeight:600, color:'#64748b' }}>สิทธิ์การใช้งาน (ปรับแต่งเพิ่มเติมได้)</label>
            </div>
            <div style={{
              display:'grid',
              gridTemplateColumns: isMobile ? '1fr' : 'repeat(4, 1fr)',
              gap:8,
            }}>
              {PERMISSIONS.map(p => (
                <label key={p.key} style={{
                  display:'flex', alignItems:'center', gap:8,
                  padding: isMobile ? '12px 14px' : '10px 12px',
                  borderRadius:8,
                  border:`1px solid ${form.permissions[p.key] ? '#0ea5e9' : '#e2e8f0'}`,
                  background: form.permissions[p.key] ? '#f0f9ff' : '#f8fafc',
                  cursor:'pointer',
                  fontSize: isMobile ? 14 : 13,
                }}>
                  <input type="checkbox" checked={!!form.permissions[p.key]}
                    onChange={e => setForm({...form, permissions:{...form.permissions, [p.key]:e.target.checked}})}
                    style={{ width: 18, height: 18, flexShrink: 0 }} />
                  <span style={{ flex: 1, minWidth: 0 }}>{p.label}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Channel Access */}
          <div style={{ marginBottom: isMobile ? 16 : 20 }}>
            <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:8 }}>
              <label style={{ fontSize:12, fontWeight:600, color:'#64748b' }}>
                📡 Channel ที่เข้าถึงได้ (ไม่เลือก = ทุก Channel)
              </label>
            </div>
            <div style={{ display:'flex', flexWrap:'wrap', gap:8 }}>
              {channels.map(ch => (
                <label key={ch.id} style={{
                  display:'flex', alignItems:'center', gap:6,
                  padding: isMobile ? '8px 14px' : '7px 14px',
                  borderRadius:8,
                  border:`1px solid ${(form.channel_ids||[]).includes(ch.id) ? '#0ea5e9' : '#e2e8f0'}`,
                  background:(form.channel_ids||[]).includes(ch.id) ? '#f0f9ff' : '#f8fafc',
                  cursor:'pointer',
                  fontSize: isMobile ? 13 : 13,
                }}>
                  <input type="checkbox" checked={(form.channel_ids||[]).includes(ch.id)}
                    onChange={e => {
                      const ids = e.target.checked
                        ? [...(form.channel_ids||[]), ch.id]
                        : (form.channel_ids||[]).filter(id => id !== ch.id);
                      setForm({...form, channel_ids: ids});
                    }} />
                  {ch.type === 'line' ? '💚' : ch.type === 'telegram' ? '✈️' : ch.type === 'facebook' ? '💙' : '📡'} {ch.name}
                  {ch.tag && <span style={{ fontSize:10, color:'#92400e', background:'#fef3c7', borderRadius:4, padding:'1px 5px' }}>{ch.tag}</span>}
                </label>
              ))}
              {channels.length === 0 && <span style={{ fontSize:13, color:'#94a3b8' }}>ยังไม่มี Channel</span>}
            </div>
          </div>

          {/* Action buttons */}
          <div style={{
            display:'flex',
            gap:10,
            justifyContent: isMobile ? 'stretch' : 'flex-end',
            flexDirection: isMobile ? 'column' : 'row',
          }}>
            <button onClick={() => { setShowForm(false); setEditId(null); }}
              style={{
                padding: isMobile ? '12px 20px' : '9px 20px',
                borderRadius:8, background:'#f1f5f9', border:'none',
                cursor:'pointer', fontSize:14, fontWeight: 500,
                order: isMobile ? 2 : 1,
              }}>ยกเลิก</button>
            <button onClick={handleSave} disabled={loading}
              style={{
                display:'flex', alignItems:'center', justifyContent: 'center', gap:6,
                padding: isMobile ? '12px 24px' : '9px 24px',
                borderRadius:8, background:'#0ea5e9', color:'#fff',
                border:'none', cursor:'pointer',
                fontSize:14, fontWeight:600,
                order: isMobile ? 1 : 2,
              }}>
              <Save size={14} />{loading ? 'กำลังบันทึก...' : 'บันทึก'}
            </button>
          </div>
        </div>
      )}

      {/* Agent List */}
      <div style={{
        background:'#fff', borderRadius:14,
        boxShadow:'0 1px 6px rgba(0,0,0,0.07)',
        overflow:'hidden',
      }}>
        <div style={{
          padding: isMobile ? '14px 16px' : '16px 24px',
          borderBottom:'1px solid #f1f5f9',
        }}>
          <h2 style={{
            margin:0,
            fontSize: isMobile ? 14 : 15,
            fontWeight:600, color:'#1e293b',
          }}>รายชื่อ Admin ({agents.length})</h2>
        </div>

        {agents.length === 0 ? (
          <div style={{ padding:48, textAlign:'center', color:'#94a3b8' }}>
            <Users size={40} style={{ marginBottom:12, opacity:0.3 }} />
            <p>ยังไม่มี Admin กดปุ่ม "เพิ่ม Admin" เพื่อเริ่มต้น</p>
          </div>
        ) : isMobile ? (
          /* mobile: Card list */
          <div>
            {agents.map((agent, i) => (
              <AgentCard key={agent.id} agent={agent}
                roleInfo={getRoleInfo(agent.role)}
                expandedId={expandedId} setExpandedId={setExpandedId}
                onEdit={handleEdit} onToggle={handleToggle} onDelete={handleDelete}
                isLast={i === agents.length - 1} />
            ))}
          </div>
        ) : (
          /* desktop: ตารางเดิม */
          <table style={{ width:'100%', borderCollapse:'collapse' }}>
            <thead>
              <tr style={{ background:'#f8fafc' }}>
                {['ชื่อ','Role','สิทธิ์','สถานะ',''].map((h,i) => (
                  <th key={i} style={{ padding:'10px 16px', textAlign:'left', fontSize:11, fontWeight:600, color:'#64748b', textTransform:'uppercase' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {agents.map(agent => {
                const roleInfo = getRoleInfo(agent.role);
                const perms = agent.permissions || {};
                const activePerms = PERMISSIONS.filter(p => perms[p.key]);
                return (
                  <React.Fragment key={agent.id}>
                    <tr style={{ borderTop:'1px solid #f1f5f9' }}
                      onMouseEnter={e => e.currentTarget.style.background='#f8fafc'}
                      onMouseLeave={e => e.currentTarget.style.background=''}>
                      <td style={{ padding:'14px 16px' }}>
                        <div style={{ fontWeight:600, color: agent.is_active ? '#1e293b' : '#94a3b8', fontSize:14 }}>{agent.name}</div>
                        <div style={{ fontSize:12, color:'#94a3b8' }}>{agent.email}</div>
                      </td>
                      <td style={{ padding:'14px 16px' }}>
                        <span style={{ background:roleInfo.color+'22', color:roleInfo.color, borderRadius:6, padding:'3px 10px', fontSize:12, fontWeight:600 }}>
                          {roleInfo.label}
                        </span>
                      </td>
                      <td style={{ padding:'14px 16px' }}>
                        <div style={{ display:'flex', flexWrap:'wrap', gap:4 }}>
                          {activePerms.slice(0,3).map(p => (
                            <span key={p.key} style={{ background:'#f0f9ff', color:'#0ea5e9', borderRadius:4, padding:'2px 7px', fontSize:11 }}>{p.label}</span>
                          ))}
                          {activePerms.length > 3 && (
                            <button onClick={() => setExpandedId(expandedId === agent.id ? null : agent.id)}
                              style={{ background:'#f1f5f9', border:'none', borderRadius:4, padding:'2px 7px', fontSize:11, cursor:'pointer', color:'#64748b', display:'flex', alignItems:'center', gap:2 }}>
                              +{activePerms.length - 3} <ChevronDown size={10} />
                            </button>
                          )}
                        </div>
                      </td>
                      <td style={{ padding:'14px 16px' }}>
                        <span style={{ background: agent.is_active ? '#dcfce7' : '#fee2e2', color: agent.is_active ? '#16a34a' : '#dc2626', borderRadius:6, padding:'3px 10px', fontSize:12 }}>
                          {agent.is_active ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td style={{ padding:'14px 16px', whiteSpace:'nowrap' }}>
                        <button onClick={() => handleEdit(agent)} style={{ background:'#f1f5f9', border:'none', borderRadius:6, padding:'6px 10px', cursor:'pointer', marginRight:6 }}>
                          <Edit2 size={13} color="#64748b" />
                        </button>
                        <button onClick={() => handleToggle(agent.id)}
                          style={{ background: agent.is_active ? '#fee2e2' : '#dcfce7', border:'none', borderRadius:6, padding:'6px 10px', cursor:'pointer', marginRight:6 }}>
                          <Power size={13} color={agent.is_active ? '#dc2626' : '#16a34a'} />
                        </button>
                        <button onClick={() => handleDelete(agent.id)} style={{ background:'#fff1f2', border:'none', borderRadius:6, padding:'6px 10px', cursor:'pointer' }}>
                          <Trash2 size={13} color="#ef4444" />
                        </button>
                      </td>
                    </tr>
                    {expandedId === agent.id && (
                      <tr style={{ background:'#f8fafc' }}>
                        <td colSpan={5} style={{ padding:'8px 16px 14px 16px' }}>
                          <div style={{ display:'flex', flexWrap:'wrap', gap:4 }}>
                            {activePerms.map(p => (
                              <span key={p.key} style={{ background:'#f0f9ff', color:'#0ea5e9', borderRadius:4, padding:'3px 10px', fontSize:12 }}>{p.label}</span>
                            ))}
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

// mobile_v1: Agent card สำหรับ mobile
function AgentCard({ agent, roleInfo, expandedId, setExpandedId, onEdit, onToggle, onDelete, isLast }) {
  const perms = agent.permissions || {};
  const activePerms = PERMISSIONS.filter(p => perms[p.key]);
  const isExpanded = expandedId === agent.id;
  return (
    <div style={{
      padding: 14,
      borderBottom: isLast ? 'none' : '1px solid #f1f5f9',
      opacity: agent.is_active ? 1 : 0.65,
    }}>
      {/* Top: ชื่อ + role + status */}
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, marginBottom: 10 }}>
        <div style={{
          width: 44, height: 44, borderRadius: '50%',
          background: roleInfo.color + '22',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          color: roleInfo.color, fontWeight: 700, fontSize: 16,
          flexShrink: 0,
        }}>
          {(agent.name || 'U')[0].toUpperCase()}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontWeight: 700, fontSize: 15, color: '#1e293b', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {agent.name}
          </div>
          <div style={{ fontSize: 12, color: '#94a3b8', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {agent.email}
          </div>
          <div style={{ display: 'flex', gap: 6, marginTop: 6, flexWrap: 'wrap' }}>
            <span style={{ background: roleInfo.color + '22', color: roleInfo.color, borderRadius: 6, padding: '2px 9px', fontSize: 11, fontWeight: 600 }}>
              {roleInfo.label}
            </span>
            <span style={{
              background: agent.is_active ? '#dcfce7' : '#fee2e2',
              color: agent.is_active ? '#16a34a' : '#dc2626',
              borderRadius: 6, padding: '2px 9px', fontSize: 11, fontWeight: 600,
            }}>
              {agent.is_active ? 'Active' : 'Inactive'}
            </span>
          </div>
        </div>
      </div>

      {/* Permissions (collapsible) */}
      {activePerms.length > 0 && (
        <div style={{ marginBottom: 10 }}>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
            {(isExpanded ? activePerms : activePerms.slice(0, 3)).map(p => (
              <span key={p.key} style={{
                background: '#f0f9ff', color: '#0ea5e9',
                borderRadius: 4, padding: '3px 8px', fontSize: 11,
              }}>{p.label}</span>
            ))}
            {activePerms.length > 3 && (
              <button onClick={() => setExpandedId(isExpanded ? null : agent.id)}
                style={{
                  background: '#f1f5f9', border: 'none',
                  borderRadius: 4, padding: '3px 8px', fontSize: 11,
                  cursor: 'pointer', color: '#64748b',
                  display: 'flex', alignItems: 'center', gap: 2,
                }}>
                {isExpanded ? 'ย่อ' : `+${activePerms.length - 3}`}
                <ChevronDown size={10} style={{ transform: isExpanded ? 'rotate(180deg)' : 'none', transition: 'transform 0.15s' }} />
              </button>
            )}
          </div>
        </div>
      )}

      {/* Actions */}
      <div style={{ display: 'flex', gap: 6 }}>
        <button onClick={() => onEdit(agent)}
          style={{
            flex: 1, padding: '10px', borderRadius: 8,
            background: '#f1f5f9', border: 'none', cursor: 'pointer',
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4,
            fontSize: 13, color: '#64748b', fontWeight: 500,
          }}>
          <Edit2 size={14} /> แก้ไข
        </button>
        <button onClick={() => onToggle(agent.id)}
          style={{
            flex: 1, padding: '10px', borderRadius: 8,
            background: agent.is_active ? '#fee2e2' : '#dcfce7',
            border: 'none', cursor: 'pointer',
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4,
            fontSize: 13, color: agent.is_active ? '#dc2626' : '#16a34a', fontWeight: 500,
          }}>
          <Power size={14} /> {agent.is_active ? 'ปิด' : 'เปิด'}
        </button>
        <button onClick={() => onDelete(agent.id)}
          style={{
            padding: '10px 14px', borderRadius: 8,
            background: '#fff1f2', border: 'none', cursor: 'pointer',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
          <Trash2 size={14} color="#ef4444" />
        </button>
      </div>
    </div>
  );
}

const Label = ({ children }) => (
  <label style={{
    fontSize: 12, fontWeight: 600, color: '#64748b',
    display: 'block', marginBottom: 4,
  }}>{children}</label>
);

const inputStyle = (isMobile) => ({
  width: '100%',
  padding: isMobile ? '12px 14px' : '9px 12px',
  borderRadius: 8,
  border: '1px solid #e2e8f0',
  fontSize: isMobile ? 16 : 14,
  boxSizing: 'border-box',
  outline: 'none',
});
